/**
 * Shared TanStack Query client.
 *
 * Defaults are tuned for internal apps that change infrequently:
 *   - staleTime 1 min so navigating between pages doesn't refetch constantly
 *   - no auto-refetch on window focus (more startling than helpful)
 *   - no retries on 401/403 — let ProtectedRoute redirect instead
 *
 * Any query that learns the session is over (a 401, or a 403 for a deactivated
 * account) re-checks `["me"]`, which then reads `null`, and the route guards
 * send the user to /login. Mutations are left out on purpose: the login form's
 * own 401 is a wrong password, not a lost session.
 */

import { type Query, QueryCache, QueryClient, type QueryKey } from "@tanstack/react-query";

import { logger } from "@/lib/logger";

import { apiErrorDetail, apiErrorStatus } from "./client";

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: recheckSessionOnAuthError }),
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const status = (error as { status?: number } | undefined)?.status;
        if (status === 401 || status === 403) {
          return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Declared as a function so it can reach `queryClient`, which exists by the
 * time any query fails. `exact` keeps a failing `["me", "detail"]` from
 * invalidating itself in a loop, and `["me"]` itself never triggers it.
 */
function recheckSessionOnAuthError(error: unknown, query: Query<unknown, unknown, unknown>) {
  // A new NDA version was published since the client accepted: re-check it, and the
  // portal's NDA guard sends them to /portal/nda.
  if (apiErrorStatus(error) === 403 && apiErrorDetail(error) === "nda_required") {
    void queryClient.invalidateQueries({ queryKey: ["portal", "nda"], exact: true });
    return;
  }
  if (isMeKey(query.queryKey) || !endsSession(error)) {
    return;
  }
  logger.debug("[auth] session ended on", query.queryKey, "→ re-checking /auth/me");
  void queryClient.invalidateQueries({ queryKey: ["me"], exact: true });
}

function endsSession(error: unknown): boolean {
  const status = apiErrorStatus(error);
  return status === 401 || (status === 403 && apiErrorDetail(error) === "account_disabled");
}

function isMeKey(queryKey: QueryKey): boolean {
  return queryKey.length === 1 && queryKey[0] === "me";
}
